import React, { useState, useEffect } from 'react';
import WidgetWrapper from './WidgetWrapper';
import { users } from '../../services/api';

const TeamWidget = ({ darkMode = false, onError }) => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 800;
  
  const [userList, setUserList] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, sessionsRes] = await Promise.all([
          users.list(),
          fetch('/api/sessions/active', { credentials: 'include' }).then(r => r.json())
        ]);

        setUserList(usersRes.data || []);
        // sessions/active returns {sessions: [...]} directly
        setActiveSessions(sessionsRes.sessions || []);
      } catch (error) {
        console.error('TeamWidget error:', error);
        onError?.('TeamWidget', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onError]);

  // Heartbeat to keep session alive and detect idle status
  useEffect(() => {
    const heartbeat = () => {
      fetch('/api/sessions/touch', { 
        method: 'POST',
        credentials: 'include'
      }).catch(() => {}); // Silent fail
    };
    
    // Touch session every minute
    const interval = setInterval(heartbeat, 60000);
    heartbeat(); // Initial touch
    
    return () => clearInterval(interval);
  }, []);

  // Refresh sessions every 30 seconds to update status
  useEffect(() => {
    const refreshSessions = async () => {
      try {
        const res = await fetch('/api/sessions/active', { credentials: 'include' });
        const data = await res.json();
        setActiveSessions(data.sessions || []);
      } catch (e) {}
    };
    
    const interval = setInterval(refreshSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const textColor = darkMode ? '#f3f4f6' : '#111827';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const getUserStatus = (user) => {
    const session = activeSessions.find(s => s.userId === user.id);
    
    if (!user.isActive) return <span style={{ color: "#9ca3af" }}>Disabled</span>;
    if (!session) return <span style={{ color: "#6b7280" }}>○ Offline</span>;
    
    // Has active session - check lastActivity for Online/Idle
    const lastActivity = new Date(session.lastActivity);
    const now = new Date();
    const diffMins = Math.floor((now - lastActivity) / 60000);
    
    if (diffMins < 2) return <span style={{ color: "#22c55e" }}>● Online</span>;
    if (diffMins < 10) return <span style={{ color: "#eab308" }}>○ Idle {diffMins}m</span>;
    return <span style={{ color: "#6b7280" }}>○ Offline</span>;
  };

  const getLastLogin = (user) => {
    if (!user.isActive) return <span style={{ color: "#9ca3af" }}>-</span>;
    if (!user.lastLogin) return <span style={{ color: "#6b7280" }}>Never</span>;
    
    const last = new Date(user.lastLogin);
    const now = new Date();
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return <span style={{ color: "#6b7280" }}>Just now</span>;
    if (diffMins < 60) return <span style={{ color: "#6b7280" }}>{diffMins}m ago</span>;
    if (diffHours < 24) return <span style={{ color: "#6b7280" }}>{diffHours}h ago</span>;
    if (diffDays < 7) return <span style={{ color: "#6b7280" }}>{diffDays}d ago</span>;
    
    // Show date for older logins
    return <span style={{ color: "#6b7280" }}>{last.toLocaleDateString()}</span>;
  };

  return (
    <WidgetWrapper title="👥 Team Activity" loading={loading} darkMode={darkMode}>
      {userList.length === 0 ? (
        <p style={{ color: subTextColor, textAlign: "center", padding: 20 }}>No users</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
              <th style={{ textAlign: "left", padding: isMobile ? 4 : 8, color: subTextColor, fontSize: isMobile ? 10 : 12 }}>User</th>
              <th style={{ textAlign: "left", padding: isMobile ? 4 : 8, color: subTextColor, fontSize: isMobile ? 10 : 12 }}>Role</th>
              <th style={{ textAlign: "left", padding: isMobile ? 4 : 8, color: subTextColor, fontSize: isMobile ? 10 : 12 }}>Status</th>
              <th style={{ textAlign: "left", padding: isMobile ? 4 : 8, color: subTextColor, fontSize: isMobile ? 10 : 12 }}>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {userList.map(user => (
              <tr key={user.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                <td style={{ padding: isMobile ? 4 : 8, fontSize: isMobile ? 10 : 12, color: textColor }}>{user.name || user.username}</td>
                <td style={{ padding: isMobile ? 4 : 8, fontSize: isMobile ? 10 : 12, color: textColor }}>{user.role}</td>
                <td style={{ padding: isMobile ? 4 : 8, fontSize: isMobile ? 10 : 12 }}>{getUserStatus(user)}</td>
                <td style={{ padding: isMobile ? 4 : 8, fontSize: isMobile ? 10 : 12 }}>{getLastLogin(user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </WidgetWrapper>
  );
};

// Widget metadata
TeamWidget.widgetId = 'team';
TeamWidget.title = '👥 Team Activity';
TeamWidget.defaultSize = { cols: 4 };
TeamWidget.category = 'business';

export default TeamWidget;